import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

interface AdminLayoutProps {
  children: React.ReactNode;
}

const menuItems = [
  {
    icon: 'ri-dashboard-line',
    label: '首页看板',
    path: '/admin',
  },
  {
    icon: 'ri-user-settings-line',
    label: '用户管理',
    path: '/admin/users',
  },
  {
    icon: 'ri-file-list-3-line',
    label: '供给审核',
    path: '/admin/supply',
  },
  {
    icon: 'ri-file-search-line',
    label: '需求审核',
    path: '/admin/demand',
  },
  {
    icon: 'ri-alert-line',
    label: '举报管理',
    path: '/admin/reports',
  },
  {
    icon: 'ri-line-chart-line',
    label: '统计分析',
    path: '/admin/analytics',
  },
  {
    icon: 'ri-newspaper-line',
    label: '资讯管理',
    path: '/admin/news',
  },
  {
    icon: 'ri-settings-3-line',
    label: '系统设置',
    path: '/admin/settings',
  },
];

export default function AdminLayout({ children }: AdminLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/admin/login');
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background-50">
      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-50 flex flex-col bg-background-950 text-white transition-all duration-300 ${
          sidebarCollapsed ? 'w-16' : 'w-60'
        } ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
      >
        {/* Logo area */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-white/10 flex-shrink-0">
          {!sidebarCollapsed && (
            <span className="text-base font-semibold tracking-wide whitespace-nowrap">
              绿证通 · 管理后台
            </span>
          )}
          {sidebarCollapsed && (
            <span className="text-lg font-bold mx-auto">G</span>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden md:flex items-center justify-center w-8 h-8 rounded-md hover:bg-white/10 transition-colors text-white/70 hover:text-white cursor-pointer"
          >
            <i className={`text-sm ${sidebarCollapsed ? 'ri-menu-unfold-line' : 'ri-menu-fold-line'}`} />
          </button>
        </div>

        {/* Menu items */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {menuItems.map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <button
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  isActive
                    ? 'bg-white/15 text-white font-medium'
                    : 'text-white/60 hover:bg-white/8 hover:text-white/85'
                }`}
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={`${item.icon} text-base`} />
                </span>
                {!sidebarCollapsed && <span className="text-sm">{item.label}</span>}
              </button>
            );
          })}
        </nav>

        {/* Bottom */}
        <div className="border-t border-white/10 p-3 flex-shrink-0">
          <button
            onClick={() => navigate('/')}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-white/50 hover:bg-white/8 hover:text-white/75 transition-colors cursor-pointer whitespace-nowrap`}
          >
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-left-line text-sm" />
            </span>
            {!sidebarCollapsed && <span className="text-sm">返回前台</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex flex-col flex-1 min-w-0">
        {/* Top bar */}
        <header className="h-16 flex items-center justify-between px-4 md:px-6 border-b border-background-200 bg-white flex-shrink-0">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-menu-line text-lg" />
          </button>

          <div className="flex-1" />

          <div className="flex items-center gap-4">
            <div className="hidden sm:flex items-center gap-2 text-sm text-foreground-600">
              <i className="ri-admin-line" />
              <span>管理员</span>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-logout-box-r-line" />
              <span className="hidden sm:inline">退出登录</span>
            </button>
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 overflow-y-auto bg-background-50">
          {children}
        </main>
      </div>
    </div>
  );
}